// Wheel handling shared by the studio plan (CIN-15118) and the booking seat map (CIN-15190). Pure and
// dependency-free so `node --test` loads it.

type WheelLike = {
    deltaX: number;
    deltaY: number;
    deltaMode: number;
    ctrlKey: boolean;
    metaKey: boolean;
    /** Legacy Chrome/Safari field: multiples of 120 for a notched mouse wheel. */
    wheelDeltaY?: number;
};

/**
 * A notched mouse wheel zooms, a trackpad two-finger scroll pans, and pinch (which browsers send
 * as ctrl+wheel) or a held cmd/ctrl zooms either way. Firefox reports line-mode deltas for
 * notched wheels; Chrome and Safari report integer multiples of 120 in `wheelDeltaY`.
 */
export function classifyWheel(event: WheelLike): 'zoom' | 'pan' {
    if (event.ctrlKey || event.metaKey) return 'zoom';
    if (event.deltaMode === 1 || event.deltaMode === 2) return 'zoom';
    if (event.deltaX !== 0) return 'pan';
    if (typeof event.wheelDeltaY === 'number' && event.wheelDeltaY !== 0) {
        return Math.abs(event.wheelDeltaY) % 120 === 0 ? 'zoom' : 'pan';
    }
    return Number.isInteger(event.deltaY) && Math.abs(event.deltaY) >= 100 ? 'zoom' : 'pan';
}

/** Multiplicative zoom factor for one wheel event; pinch deltas are tiny and need a stronger gain. */
export function wheelZoomFactor(event: WheelLike): number {
    const lineHeight = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 400 : 1;
    const delta = event.deltaY * lineHeight;
    const gain = event.ctrlKey && !Number.isInteger(event.deltaY) ? 0.01 : event.ctrlKey ? 0.008 : 0.0015;
    return Math.exp(-Math.max(-300, Math.min(300, delta)) * gain);
}
